// @ts-nocheck
import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { FaChartLine } from 'react-icons/fa';
import heroImage from '../assets/metal-blocks-shape-shint-blue-and-yellow 1.png';
import trophyIcon from '../assets/ix_trophy.png';
import vectorIcon from '../assets/Vector.png';
import growthIcon from '../assets/Vector (1).png';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { 
      duration: 0.7,
      ease: "easeOut"
    }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { 
      duration: 0.7,
      ease: "easeOut"
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const backgroundAnimation = {
  hidden: { opacity: 0, scale: 1.1 },
  visible: { 
    opacity: 0.5, 
    scale: 1,
    transition: { 
      duration: 1.5,
      ease: "easeOut"
    }
  }
};

const buttonAnimation = {
  scale: [1, 1.05, 1],
  boxShadow: [
    "0 0 10px rgba(0, 100, 255, 0.5)",
    "0 0 20px rgba(0, 100, 255, 0.7)",
    "0 0 10px rgba(0, 100, 255, 0.5)"
  ],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

const floatAnimation = {
  y: [0, -10, 0],
  transition: {
    duration: 2.5,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

const pulseAnimation = {
  scale: [1, 1.05, 1],
  opacity: [0.9, 1, 0.9],
  transition: {
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

const highlightContainerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.8,
      staggerChildren: 0.3
    }
  }
};

const highlightItemAnimation = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      duration: 0.7,
      type: "spring",
      stiffness: 100
    }
  }
};

const textAnimation = {
  x: [0, 20, 0],
  transition: {
    duration: 3,
    repeat: Infinity,
    ease: "linear",
    delay: 0.5
  }
};

const letterAnimation = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.5
    }
  })
};

const HeroSection = () => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start("visible");
  }, [controls]);

  const headingText = ["Empowering", "startups to", "succeed in", "traditional and", "web3 ecosystems"];

  // Function to scroll to footer contact section
  const scrollToContact = () => {
    const footerSection = document.getElementById('footer-section');
    if (footerSection) {
      footerSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center pt-20 overflow-hidden relative bg-black">
      {/* Background Image with Animation */}
      <div className="absolute inset-0 w-full h-full flex items-center justify-center">
        <motion.div 
          className="w-[80%] h-[80%] relative"
          initial="hidden"
          animate="visible"
          variants={backgroundAnimation}
        >
          <motion.img 
            src={heroImage}
            alt="Hero Background"
            className="w-full h-full object-contain opacity-50"
            animate={{
              rotate: [0, 1, 0],
              scale: [1, 1.02, 1]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          {/* Overlay to ensure text readability */}
          <div className="absolute inset-0 bg-black/50"></div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="lg:col-span-7"
          >
            <div className="text-[32px] md:text-[45px] lg:text-[55px] font-montserrat font-normal leading-[100%] tracking-normal w-full lg:w-[520px] h-auto lg:h-[335px] text-white flex flex-col space-y-1">
              {headingText.map((text, index) => (
                <motion.div
                  key={index}
                  custom={index}
                  variants={fadeInLeft}
                  className="overflow-hidden"
                >
                  <motion.span
                    initial={{ y: 40 }}
                    animate={{ y: 0 }}
                    transition={{ 
                      delay: index * 0.1,
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                    className="inline-block"
                  >
                    {text}
                  </motion.span>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              variants={fadeInUp}
              className="mt-8"
            >
              <motion.button 
                className="btn btn-primary mr-4 shadow-neon rounded-full"
                animate={buttonAnimation}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToContact}
              >
                Start Project
              </motion.button>
            </motion.div>
            
            {/* Key Highlights */}
            <motion.div 
              variants={highlightContainerAnimation}
              initial="hidden"
              animate="visible"
              className="flex flex-col md:flex-row w-full max-w-5xl mx-auto mt-16 md:mt-28 px-5 md:px-8 md:gap-10 lg:gap-16"
            >
              {[
                { icon: <img src={trophyIcon} alt="Trophy" className="w-6 h-6 md:w-7 md:h-7 lg:w-9 lg:h-9 xl:w-10 xl:h-10" />, text: "22 years of expertise" },
                { icon: <img src={vectorIcon} alt="Custom Strategies" className="w-6 h-6 md:w-7 md:h-7 lg:w-9 lg:h-9 xl:w-10 xl:h-10" />, text: "Custom Strategies" },
                { icon: <img src={growthIcon} alt="Growth" className="w-6 h-6 md:w-7 md:h-7 lg:w-9 lg:h-9 xl:w-10 xl:h-10" />, text: "Growth for the future" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={highlightItemAnimation}
                  className={`flex flex-row items-center mb-6 md:mb-0 md:flex-1 justify-center md:justify-start space-x-6 md:space-x-5 lg:space-x-6 ${index > 0 ? 'md:pl-4 lg:pl-6' : ''}`}
                  whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                >
                  <motion.div 
                    className="text-primary shrink-0"
                    animate={floatAnimation}
                  >
                    {item.icon}
                  </motion.div>
                  <motion.p 
                    className="font-medium text-gray-400 text-base md:text-sm lg:text-base xl:text-lg whitespace-nowrap"
                    animate={pulseAnimation}
                  >
                    {item.text}
                  </motion.p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Right Content - Tagline */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInRight}
            className="hidden lg:flex flex-col justify-center items-start space-y-0.5 w-[234px] h-[87px] lg:col-span-3 lg:col-start-10 mt-32 ml-24"
          >
            <motion.p 
              className="w-full h-full font-montserrat font-normal text-[18px] md:text-[20px] lg:text-[24px] leading-[100%] tracking-normal text-white"
              whileHover={{ scale: 1.05, color: "#ffffff" }}
              transition={{ duration: 0.2 }}
            >
              Unleashing global potential rooted in local expertise
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection; 